import { useMutation, useQueryClient } from "@tanstack/react-query";

import { translate } from "app/filters/i18nFilter";
import { getQueryKey } from "app/modules/api-client";
import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { ACTIVATION_OBJECT_QUERY_KEY } from "app/pages/activation/queries/data/useActivationObjectQuery";
import { SHORT_ACTIVATION_OBJECTS_QUERY_KEY } from "app/pages/activation/queries/data/useShortActivationSetListQuery";
import { TOPICS_QUERY_KEY } from "app/pages/activation/queries/topics/useGetTopicsDataQuery";
import { SUGGESTIONS_QUERY_KEY } from "app/pages/activation/queries/topics/useTopicSuggestions";
import { createActivationApi } from "app/pages/activation/services/api";
import type { ActivationObjectSchema, FirstPartyDataSource } from "app/pages/activation/types/api";

const useSaveSettingsMutation = (activationId: string) => {
    const { showErrorToast } = useToasts();
    const queryClient = useQueryClient();

    return useMutation<ActivationObjectSchema, Error, unknown>({
        mutationFn: async (params: {
            id: string;
            country: number;
            domain: string;
            competitorsDomains: string[] | null;
            name: string;
            trafficSource?: FirstPartyDataSource;
        }) => {
            const { id, country, domain, competitorsDomains, name, trafficSource } = params;
            const api = createActivationApi();

            return api.activation.update({
                id,
                country: country,
                primaryDomain: domain,
                competitorsDomains: competitorsDomains,
                name,
                trafficSource,
            });
        },
        onSuccess() {
            void queryClient.invalidateQueries({
                queryKey: getQueryKey({
                    tag: "Activation",
                    methodName: "userdataActivationDetail",
                    args: [activationId],
                }),
            });
            void queryClient.invalidateQueries({
                queryKey: getQueryKey({ tag: "Alerts" }),
            });
            void queryClient.invalidateQueries({ queryKey: [ACTIVATION_OBJECT_QUERY_KEY, activationId] });
            void queryClient.invalidateQueries({ queryKey: [TOPICS_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [SUGGESTIONS_QUERY_KEY] });
            void queryClient.invalidateQueries({ queryKey: [SHORT_ACTIVATION_OBJECTS_QUERY_KEY] });
        },
        onError(error: Error) {
            showErrorToast(translate("common.api.error", { error: error?.message }));
        },
    });
};

export default useSaveSettingsMutation;
